import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Navbar from "../components/homepage/Navbar";
import { Eye, Target, Cpu, Users, Landmark, Rocket, X } from "lucide-react";
 
import { useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "react-toastify";
import { Header, Footer } from "../shared";

const modalRootId = "modal-root";
const API_URL = process.env.REACT_APP_API_URL;

const ensureModalRoot = () => {
  let root = document.getElementById(modalRootId);
  if (!root) {
    root = document.createElement("div");
    root.id = modalRootId;
    document.body.appendChild(root);
  }
  return root;
};

function useLockBodyScroll(isLocked) {
  useEffect(() => {
    if (!isLocked) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isLocked]);
}

const SIZES = {
  sm: "max-w-md",
  md: "max-w-2xl",
  lg: "max-w-4xl",
  xl: "max-w-6xl",
  full: "w-full h-full",
};

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  closeOnBackdrop = true,
  ariaLabel,
  disableClose = false,
}) => {
  const root = ensureModalRoot();
  useLockBodyScroll(Boolean(isOpen));

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape" && isOpen) {
        if (!disableClose && onClose) onClose();
      }
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose, disableClose]);

  if (!root) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              if (!disableClose && closeOnBackdrop && onClose) onClose();
            }}
            aria-hidden="true"
          />

          {/* Modal panel */}
          <motion.div
            className={`relative mx-4 ${SIZES[size]}`}
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            role="dialog"
            aria-modal="true"
            aria-label={
              ariaLabel ?? (typeof title === "string" ? title : "Modal dialog")
            }
          >
            <div className="relative rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 overflow-hidden">
              {/* Header */}
              {(title || !disableClose) && (
                <div className="flex items-center justify-between px-6 py-4 border-b">
                  <h3 className="text-lg font-semibold text-[#193c54]">
                    {title}
                  </h3>

                  {!disableClose && onClose && (
                    <button
                      onClick={onClose}
                      aria-label="Close modal"
                      className="ml-4 inline-flex h-9 w-9 items-center justify-center rounded-lg hover:bg-slate-100 "
                    >
                      <X size={20} className="text-slate-600" />
                    </button>
                  )}
                </div>
              )}

              {/* Body */}
              <div className="px-6 py-6">{children}</div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    root,
  );
};

const ecosystemData = [
  {
    label: "WHAT IT IS",
    title: "What It Is",
    description:
      "Drone City Haryana is a future-ready drone technology park bringing together component manufacturing, drone assembly, flying infrastructure, training, repair capability and innovation under one integrated ecosystem.",
  },
  {
    label: "WHY IT MATTERS",
    title: "Why It Matters",
    description:
      "The drone industry requires integrated infrastructure to scale. Drone City Haryana provides manufacturing capability, testing zones, training facilities and industrial capacity in one location.",
  },
  {
    label: "WHY HISAR",
    title: "Why Hisar",
    description:
      "Hisar provides a strategic advantage with land availability, logistics connectivity, airport-led growth and an environment suitable for large-scale drone manufacturing and testing.",
  },
  {
    label: "JOINT VENTURE",
    title: "Joint Venture Model",
    description:
      "The project operates under a strategic joint venture led by AVPL International with Drone Tech Multi State Co-operative Society and industry stakeholders to create a long-term industrial ecosystem.",
  },
  {
    label: "VISION",
    title: "Vision",
    description:
      "To build India’s most integrated drone city — a destination where manufacturing, training, testing, innovation and global collaboration come together in one future-ready ecosystem.",
  },
  {
    label: "INDUSTRY IMPACT",
    title: "Industry Impact",
    description:
      "Drone City Haryana will accelerate drone manufacturing scale, component localization, pilot training, rural employment and export-oriented drone technology growth.",
  },
];

const bubbles = [
  { size: 120, x: "5%", y: "70%", delay: 0 },
  { size: 80, x: "80%", y: "75%", delay: 1 },
  { size: 200, x: "20%", y: "0", delay: 2 },
  { size: 120, x: "70%", y: "25%", delay: 3 },
  { size: 90, x: "50%", y: "70%", delay: 4 },
];

const BubbleBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {bubbles.map((bubble, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full bg-gradient-to-b from-[#a2affb] to-[#c2cafc] backdrop-blur-sm"
          style={{
            width: bubble.size,
            height: bubble.size,
            left: bubble.x,
            top: bubble.y,
          }}
          animate={{
            y: [0, -20, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            delay: bubble.delay,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

const capabilities = [
  {
    title: "Component Manufacturing",
    description:
      "Manufacturing ecosystem for drone components supporting the full value chain.",
  },
  {
    title: "Drone Assembly Units",
    description:
      "Facilities designed for scalable drone assembly and production.",
  },
  {
    title: "Pilot & Technician Training",
    description:
      "Training infrastructure for pilots, technicians and drone industry talent.",
  },
  {
    title: "Testing & Flying Infrastructure",
    description:
      "Dedicated flying fields and testing environments for safe operations.",
  },
  {
    title: "Repair & Maintenance Support",
    description: "Maintenance, repair and lifecycle support for drone fleets.",
  },
  {
    title: "Innovation & R&D Collaboration",
    description:
      "Research collaboration and product innovation within the ecosystem.",
  },
];

const About = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  return (
    <section className="py-8 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* TOP ROW */}
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-8">
          {/* LEFT */}
          <div className="max-w-xl space-y-6">
            <span className="inline-block text-lg px-4 py-3 bg-blue-100 text-[#1e3a5f] font-bold rounded-full">
              About Drone City Haryana
            </span>

            <p className="text-gray-600 leading-relaxed">
              Drone City Haryana is being developed as a complete platform for
              the drone industry. It brings together manufacturing, assembly,
              training, testing, repair and innovation within one integrated
              technology park.
            </p>
          </div>

          {/* RIGHT */}
          <div className="max-w-lg lg:ml-auto">
            <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 leading-tight">
              One Ecosystem. One Destination.
            </h2>

            <a
              href="#gallery"
              className="mt-2 inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[#193c54] text-white font-medium hover:scale-105 transition"
            >
              Discover the Ecosystem
            </a>
          </div>
        </div>
        {/* CONTENT ROW */}
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          {/* LEFT GRID */}
          <div className="grid grid-cols-2 gap-4 relative">
            {capabilities.map((item, index) => {
              const isActive = index === activeIndex;
              const isLeftCard = index % 2 === 0;

              return (
                <div
                  key={index}
                  className={`relative md:h-[100px] ${activeIndex !== null && !isActive ? "z-0" : "z-10"}`}
                  onClick={() => setActiveIndex(isActive ? null : index)}
                >
                  <div
                    className={`cursor-pointer p-2 rounded-xl border transition-all duration-500 ease-out h-full
          ${
            isActive
              ? `absolute z-20 bg-[#193c54] text-white border-black w-[calc(200%+1rem)] h-full
                ${isLeftCard ? "left-0" : "-left-full"}`
              : "bg-white border-gray-200 hover:bg-gray-100"
          }`}
                  >
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold italic text-wrap">
                        {item.title}
                      </h4>
                    </div>

                    {isActive && (
                      <p className="text-sm text-gray-30 max-w-md text-wrap">
                        {item.description}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* RIGHT VIDEO */}
          <div className="rounded-xl overflow-hidden shadow-lg">
            <video
              src="/drone-city/park-tour.webm"
              poster="/drone-city/poster.webp"
              className="w-full h-[336px] object-cover"
              autoPlay
              muted
              loop
              playsInline
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const PositionStrip = () => {
  const items = [
    "India’s First Drone City",
    "Integrated Drone Ecosystem",
    "Manufacturing • Testing • Training",
    "Built for India. Ready for the World",
  ];

  return (
    <section className="relative bg-[#abb6fb] overflow-hidden py-4 border-y border-slate-700/30">
      <div className="flex whitespace-nowrap">
        <div className="flex animate-marquee items-center">
          {[...items, ...items].map((item, i) => (
            <div key={i} className="flex px-8 border-r border-[#1e3a5f]/20">
              <p className="text-sm md:text-lg font-bold tracking-wide text-[#1e3a5f] uppercase italic">
                {item}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* 4. CSS Keyframe */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 25s linear infinite;
        }
        /*  Pause on hover so users can read */
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
};

const ContactFormModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const res = await axios.post(`${API_URL}/partner`, data);

      toast.success("Registration submitted successfully!");

      reset();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Partner With Us" size="lg">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>

          <input
            type="text"
            placeholder="Enter your name"
            {...register("name", { required: "Name is required" })}
            className={`w-full border rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-[#193c54]
      ${errors.name ? "border-red-500" : "border-gray-300"}
      `}
          />

          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number <span className="text-red-500">*</span>
          </label>

          <input
            type="tel"
            placeholder="Enter phone number"
            maxLength={10}
            {...register("phone", {
              required: "Phone number is required",
              pattern: {
                value: /^[6-9]\d{9}$/,
                message: "Enter valid phone number",
              },
            })}
            className={`w-full border rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-[#193c54]
      ${errors.phone ? "border-red-500" : "border-gray-300"}
      `}
          />

          {errors.phone && (
            <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-red-500">*</span>
          </label>

          <input
            type="email"
            placeholder="Enter your email"
            {...register("email", {
              required: "Email is required",
            })}
            className={`w-full border rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-[#193c54]
      ${errors.email ? "border-red-500" : "border-gray-300"}
      `}
          />

          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
          )}
        </div>

        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address <span className="text-red-500">*</span>
          </label>

          <input
            placeholder="Enter your address"
            {...register("address", {
              required: "Address is required",
            })}
            className={`w-full border rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-[#193c54]
      ${errors.address ? "border-red-500" : "border-gray-300"}
      `}
          />

          {errors.address && (
            <p className="text-red-500 text-sm mt-1">
              {errors.address.message}
            </p>
          )}
        </div>

        {/* Submit */}
        <div className="md:col-span-2 flex justify-center pt-2">
          <button
            type="submit"
            disabled={loading}
            className="bg-[#193c54] text-white px-8 py-3 rounded-lg hover:bg-[#0f2c40] transition font-medium shadow-md"
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const Hero = () => {
  const [openForm, setOpenForm] = useState(false);
  return (
    <section className="relative min-h-[calc(100vh-80px)] pt-12 w-full overflow-hidden bg-[#c3ccff] flex items-center">
      <BubbleBackground />
      {/* Background Huge Text */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <h1 className="text-[14vw] font-extrabold text-gray-200 tracking-tight select-none">
          DRONE CITY
        </h1>
      </div>

      {/* Container */}
      <div className="relative z-10 mx-auto w-full max-w-7xl px-6">
        <div className="flex flex-col items-center text-center">
          {/* Tagline */}
          <p className="text-gray-800 font-semibold my-2">
            Future Tech Park by AVPL
          </p>

          {/* Heading */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-3xl md:text-5xl font-bold text-gray-900"
          >
            India’s Drone Future. Built at Scale.
          </motion.h2>

          {/* Description */}
          <p className="mt-4 max-w-2xl text-gray-800">
            A world-class drone manufacturing and technology ecosystem coming up
            in Sisai, Hisar — engineered for domestic demand, exports, testing,
            training and industrial innovation.
          </p>

          {/* Drone Image */}
          <motion.img
            src="/drone-city/drone.png"
            alt="Drone"
            className="w-[480px] md:w-[650px] drop-shadow-2xl"
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
          />

          {/* CTA Buttons */}
          <div className="flex gap-4 mb-10 flex-wrap justify-center">
            <a
              href="#gallery"
              className="px-6 py-3 rounded-full bg-[#193c54] text-white shadow-md hover:scale-105 transition"
            >
              Explore The Park
            </a>

            <button
              onClick={() => setOpenForm(true)}
              className="px-6 py-3 rounded-full border border-gray-300 hover:bg-gray-100 transition"
            >
              Partner With Us
            </button>
          </div>
        </div>
      </div>
      <ContactFormModal isOpen={openForm} onClose={() => setOpenForm(false)} />
    </section>
  );
};

const polarToCartesian = (cx, cy, r, angle) => {
  const rad = ((angle - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
};

const describeArc = (cx, cy, innerR, outerR, startAngle, endAngle) => {
  const startOuter = polarToCartesian(cx, cy, outerR, endAngle);
  const endOuter = polarToCartesian(cx, cy, outerR, startAngle);

  const startInner = polarToCartesian(cx, cy, innerR, endAngle);
  const endInner = polarToCartesian(cx, cy, innerR, startAngle);

  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

  return `
    M ${startOuter.x} ${startOuter.y}
    A ${outerR} ${outerR} 0 ${largeArcFlag} 0 ${endOuter.x} ${endOuter.y}
    L ${endInner.x} ${endInner.y}
    A ${innerR} ${innerR} 0 ${largeArcFlag} 1 ${startInner.x} ${startInner.y}
    Z
  `;
};

const EcosystemDiagram = ({ data, activeIndex, onSelect }) => {
  const segments = data.map((item) => item.label);

  const cx = 320;
  const cy = 320;

  const hubRadius = 120;
  const innerRadius = 170;
  const outerRadius = 300;

  const sliceAngle = 360 / segments.length;

  return (
    <div className="relative w-full sm:w-[480px] md:w-[500px] h-fit">
      <svg viewBox="0 0 640 640" className="w-full h-full">
        {/* Hub */}
        <circle cx={cx} cy={cy} r={hubRadius} fill="#1e3a5f" />

        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          fill="white"
          fontSize="20"
          fontWeight="600"
        >
          Future Tech Park
        </text>

        {segments.map((label, i) => {
          const startAngle = i * sliceAngle;
          const endAngle = startAngle + sliceAngle;
          const midAngle = startAngle + sliceAngle / 2;

          const path = describeArc(
            cx,
            cy,
            innerRadius,
            outerRadius,
            startAngle + 3,
            endAngle - 3,
          );

          const textPos = polarToCartesian(cx, cy, 235, midAngle);

          const isActive = i === activeIndex;

          return (
            <g key={i} onClick={() => onSelect(i)} className="cursor-pointer">
              <path
                d={path}
                fill={isActive ? "#8997F0" : "transparent"}
                stroke={isActive ? "#FFFFFF" : "#FFFFFF"}
                strokeWidth="3"
                className="transition-all duration-300"
              />

              <text
                x={textPos.x}
                y={textPos.y}
                textAnchor="middle"
                fill={isActive ? "#FFFFFF" : "#1e3a5f"}
                fontSize="14"
                fontWeight="700"
              >
                {label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const EcosystemSection = () => {
  const intervalRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const visibleItems = [
    ecosystemData[activeIndex],
    ecosystemData[(activeIndex + 1) % ecosystemData.length],
    ecosystemData[(activeIndex + 2) % ecosystemData.length],
  ];

  const startAutoRotation = () => {
    clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % ecosystemData.length);
    }, 5000);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % ecosystemData.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleSelect = (index) => {
    setActiveIndex(index);
    startAutoRotation();
  };

  return (
    <section className="bg-[#c3ccff]">
      <div className="max-w-7xl mx-auto px-4 pt-6">
        {/* Heading */}
        <div className="text-center">
          <h2 className="text-3xl font-semibold text-gray-900">
            Drone City Haryana Ecosystem
          </h2>

          <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
            Integrated supply chain powering India’s drone industry from one
            unified destination.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 py-12 items-center">
          {/* Diagram */}
          <div className="flex justify-center">
            <EcosystemDiagram
              data={ecosystemData}
              activeIndex={activeIndex}
              onSelect={handleSelect}
            />
          </div>

          {/* Dynamic Content */}
          <div className="max-w-lg mx-auto">
            {visibleItems.map((item, index) => (
              <div key={index} className="border-b border-gray-200 pb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>

                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const WhyItMatters = () => {
  const challenges = [
    "Manufacturing in separate locations",
    "Testing infrastructure limitations",
    "Limited training facilities",
    "Fragmented supply chains",
  ];

  const solutions = [
    "Integrated manufacturing ecosystem",
    "Dedicated testing infrastructure",
    "Pilot & technician training programs",
    "Unified drone industry supply chain",
  ];

  return (
    <section className="bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Heading */}
        <div className="grid md:grid-cols-2 gap-10 mb-10">
          <div className="flex items-center justify-center text-2xl md:text-3xl px-6 py-8 bg-blue-100 text-[#1e3a5f] font-bold rounded-full">
            Why It Matters
          </div>

          <div className="flex items-center">
            <p className="text-gray-600 leading-relaxed w-full">
              The drone industry cannot scale with fragmented infrastructure.
              Drone City Haryana creates a unified ecosystem where
              manufacturing, testing, training and innovation operate together.
            </p>
          </div>
        </div>

        {/* Comparison Layout */}
        <div className="relative grid md:grid-cols-2 gap-12 items-center">
          {/* Industry Challenge */}
          <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
            <h3 className="text-2xl font-semibold mb-6 text-gray-900">
              Industry Challenge
            </h3>

            <ul className="space-y-4">
              {challenges.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-700">
                  <span className="mt-1 w-5 h-5 flex items-center justify-center rounded-full bg-red-100 text-red-500 text-xs">
                    ✕
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Drone City Solution */}
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-8 shadow-sm">
            <h3 className="text-2xl font-semibold mb-6 text-sky-700">
              Drone City Haryana Solution
            </h3>

            <ul className="space-y-4">
              {solutions.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-800">
                  <span className="mt-1 w-5 h-5 flex items-center justify-center rounded-full bg-sky-100 text-sky-600 text-xs">
                    ✓
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

const WhyHisar = () => {
  const items = [
    {
      title: "Strategic location outside NCR congestion",
    },
    {
      title: "Environment suitable for drone testing",
    },
    {
      title: "Airport-led logistics growth",
    },
    {
      title: "Industrial expansion potential",
    },
  ];

  return (
    <section className="py-8 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        {/* LEFT CONTENT */}
        <div>
          <span className="inline-block px-8 py-3 text-2xl font-bold rounded-full bg-gray-200 text-[#1e3a5f] mb-6">
            WHY HISAR
          </span>

          <h2 className="text-4xl font-semibold text-gray-900 mb-6">
            Strategic Location for Drone Industry
          </h2>

          <p className="text-gray-600 max-w-md leading-relaxed">
            Hisar offers the right balance of connectivity, land availability
            and industrial growth potential. Its location supports large-scale
            drone testing, manufacturing and logistics operations.
          </p>
        </div>

        {/* RIGHT CARDS */}
        <div className="grid sm:grid-cols-2 gap-6">
          {items.map((item, i) => (
            <div
              key={i}
              className="relative h-28 rounded-xl border border-gray-200 bg-white overflow-hidden hover:shadow-md transition flex items-center"
            >
              {/* Background Image */}
              <img
                src="/drone-city/drone-pattern.jpg"
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-[0.07]"
              />

              {/* Content */}
              <div className="relative px-6">
                <p className="text-[#1e3a5f] text-lg font-bold leading-relaxed">
                  {item.title}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Impact = () => {
  const stats = [
    { number: "100+", label: "Drone Companies" },
    { number: "5000+", label: "Jobs Created" },
    { number: "Global", label: "Export Potential" },
    { number: "Future", label: "Drone Innovation Hub" },
  ];

  return (
    <section className="relative py-8 overflow-hidden">
      {/* Background Image */}
      <img
        src="/drone-city/drone-pattern.jpg"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Overlay */}
      <div className="absolute inset-0 bg-[#1e3a5f]/70"></div>

      <div className="relative max-w-7xl mx-auto px-6 text-center">
        {/* Heading */}
        <h2 className="text-white text-xl md:text-2xl font-extrabold mb-12">
          Industry Impact
        </h2>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((item, i) => (
            <div
              key={i}
              className="px-6 py-6 rounded-xl
              bg-white/10 backdrop-blur-sm
              border border-white/20
              text-white"
            >
              <h3 className="text-2xl md:text-3xl font-semibold">
                {item.number}
              </h3>

              <p className="text-sm mt-1 text-white/80">{item.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const JointVentures = () => {
  const partners = [
    {
      share: "51%",
      title: "AVPL International",
      description:
        "Strategic leadership, project execution and development of the manufacturing ecosystem.",
    },
    {
      share: "10%",
      title: "Drone Tech Multi State Co-operative Society",
      description:
        "Institutional participation supporting inclusive industry development and cooperative linkage.",
    },
    {
      share: "39%",
      title: "Drone Industry Promoters & Shareholders",
      description:
        "Technology partners, investors and industry stakeholders accelerating scale and innovation.",
    },
  ];

  return (
    <section className="pt-8 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="grid md:grid-cols-[2fr_1fr] gap-10">
          <div className="max-w-3xl mb-8">
            <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">
              Joint Venture Model
            </span>

            <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mt-3">
              Built on a Strategic Joint Venture Framework
            </h2>

            <p className="text-gray-600 mt-3 leading-relaxed">
              Drone City Haryana is being developed under a joint venture
              structure between AVPL International, Drone Tech Multi State
              Co-operative Society and industry stakeholders to build a
              long-term drone manufacturing ecosystem.
            </p>
          </div>

          {/* Why it matters */}
          <div className="text-gray-600 leading-relaxed py-12">
            <h3 className="text-xl font-semibold text-[#193c54] underline">
              Why this structure matters
            </h3>

            <p className="text-gray-600 mt-3 leading-relaxed">
              It combines entrepreneurial leadership, cooperative participation
              and industry partnership to create a stable platform for long-term
              ecosystem growth.
            </p>
          </div>
        </div>

        {/* JV Cards */}
        <div className="grid md:grid-cols-3 gap-2">
          {partners.map((p, i) => (
            <div
              key={i}
              className="bg-white border border-gray-200 rounded-xl p-3 hover:shadow-lg transition group"
            >
              <div className="text-4xl font-semibold text-blue-600 mb-2">
                {p.share}
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition">
                {p.title}
              </h3>

              <p className="text-gray-600 text-sm leading-relaxed">
                {p.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PillarCard = ({ icon: Icon, title, description, tags, image, label }) => (
  <div className="relative flex flex-col rounded-[2rem] overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-500 group">
    {/* Background Image */}
    <img
      src={image}
      alt={title}
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-30"
    />
    {/* Content */}
    <div className="relative z-10 flex flex-col flex-grow p-6 space-y-4">
      {/* Icon */}
      <div className="w-12 h-12 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-[#1e3a5f] shadow-sm">
        <Icon size={22} strokeWidth={1.5} />
      </div>

      {/* Title */}
      <h3 className="text-xl font-semibold text-black group-hover:text-[#1e3a5f] transition-colors">
        {title}
      </h3>

      {/* Description */}
      <p className="text-[#193c54] leading-relaxed font-semibold text-sm">
        {description}
      </p>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 pt-2">
        {tags.map((tag, i) => (
          <span
            key={i}
            className="text-xs font-semibold uppercase tracking-wider text-gray-500 px-2.5 py-1 bg-white/80 rounded-full border border-gray-200 group-hover:border-blue-200 transition-colors"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  </div>
);

const VisionMissionCTA = () => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="bg-[#fcfdfe] text-gray-800 selection:bg-blue-600 selection:text-white">
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* CORE PILLARS */}
        <div className="grid md:grid-cols-2 gap-8 my-4">
          <PillarCard
            icon={Eye}
            title="Our Vision"
            description="To build India’s most integrated drone city — a destination where manufacturing, training, testing, innovation and global collaboration come together in one future-ready ecosystem."
            tags={["One Ecosystem", "Integrated", "Global Destination"]}
            image="https://images.unsplash.com/photo-1508614589041-895b88991e3e?auto=format&fit=crop&q=80&w=800"
          />

          <PillarCard
            icon={Target}
            title="Our Mission"
            description="To create a world-class drone industry platform from rural Haryana that serves domestic demand, supports exports and powers the next era of Indian drone capability."
            tags={["Manufacturing", "Exports", "Rural Transformation"]}
            image="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=800"
          />
        </div>

        {/* CTA SECTION */}
        <div className="bg-white rounded-[2rem] border border-gray-200 overflow-hidden shadow-lg flex flex-col lg:flex-row my-10">
          {/* LEFT SIDE */}
          <div className="p-6 lg:w-3/5 space-y-4 border-b lg:border-b-0 lg:border-r border-gray-100">
            <span className="text-base font-semibold uppercase tracking-wider text-[#1e3a5f]">
              Partner Portal
            </span>

            <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 leading-tight">
              Let’s Build the Future of the
              <br />
              <span className="text-[#1e3a5f]">Drone Industry Together</span>
            </h2>

            <p className="text-gray-600 leading-relaxed max-w-xl">
              Drone City Haryana is the definitive platform for manufacturers
              and technology partners.
              <span className="text-gray-900 font-medium block mt-3">
                Manufacture here. Test here. Train here. Scale from here.
              </span>
            </p>

            {/* STATS */}
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-3">
              {[
                { label: "Platform JV", value: "AVPL led" },
                { label: "Market Depth", value: "Global" },
                { label: "Node Location", value: "Sisai" },
              ].map((stat, i) => (
                <div key={i}>
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                    {stat.label}
                  </div>

                  <div className="text-xl font-semibold text-gray-900">
                    {stat.value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT SIDE */}
          <div className="lg:w-2/5 p-6 bg-gray-50 flex flex-col justify-center space-y-8">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
              Ecosystem Pillars
            </span>

            <div className="space-y-6">
              {[
                { title: "Manufacturing Scale", icon: Cpu },
                { title: "Testing & Flying Fields", icon: Rocket },
                { title: "Pilot & Tech Training", icon: Users },
                { title: "JV Partnership Model", icon: Landmark },
              ].map((benefit, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-gray-500">
                    <benefit.icon size={20} strokeWidth={1.5} />
                  </div>

                  <span className="text-gray-700 text-sm font-medium">
                    {benefit.title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const Gallery = () => {
  const images = [
    "/drone-city/gallery1.jpeg",
    "/drone-city/gallery2.jpeg",
    "/drone-city/gallery3.jpeg",
    // "/drone-city/gallery4.jpeg",
    "/drone-city/gallery5.jpeg",
    "/drone-city/gallery6.jpeg",
    "/drone-city/gallery7.jpeg",
    "/drone-city/gallery8.jpeg",
    "/drone-city/gallery9.jpeg",
    "/drone-city/gallery10.jpeg",
    "/drone-city/gallery11.jpeg",
    "/drone-city/gallery12.jpeg",
    "/drone-city/gallery13.jpeg",
    // "/drone-city/gallery14.jpeg",
    "/drone-city/gallery15.jpeg",
    // "/drone-city/gallery16.jpeg",
    "/drone-city/gallery17.jpeg",
    // "/drone-city/gallery18.jpeg",
    // "/drone-city/gallery19.jpeg",
    "/drone-city/gallery20.jpeg",
    "/drone-city/gallery21.jpeg",
    "/drone-city/gallery22.jpeg",
    "/drone-city/gallery23.jpeg",
    "/drone-city/gallery24.jpeg",
    "/drone-city/gallery25.jpeg",
    "/drone-city/gallery26.jpeg",
  ];

  return (
    <section id="gallery" className="py-8 bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Heading */}
        <div className="mb-12">
          <span className="text-lg font-semibold uppercase tracking-wider text-[#193c54]">
            Gallery
          </span>

          <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mt-2">
            Drone City Development Vision
          </h2>
        </div>

        {/* Carousel */}
        <div className="overflow-hidden">
          <div className="flex gap-6 animate-gallery hover:[animation-play-state:paused] hover:cursor-pointer">
            {[...images, ...images].map((img, i) => (
              <div
                key={i}
                className="min-w-[calc(50%-1rem)] md:min-w-[calc(33.333%-1rem)] lg:min-w-[calc(25%-1.125rem)] rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm"
              >
                <img
                  src={img}
                  alt=""
                  className="w-full h-64 object-cover hover:scale-105 transition duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
    @keyframes gallery-scroll {
      0% { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }

    .animate-gallery {
      animation: gallery-scroll 10s linear infinite;
    }
  `}</style>
    </section>
  );
};

const DroneCityHaryana = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);
  return (
    <div className="bg-slate-50 text-gray-900">
      <Header />
      <Hero />
      <PositionStrip />
      <EcosystemSection />
      <About />
      <WhyItMatters />
      <WhyHisar />
      <Impact />
      <JointVentures />
      <VisionMissionCTA />
      <Gallery />
      <Footer />
    </div>
  );
};

export default DroneCityHaryana;
